import { useEffect, useState } from 'react';
import { Box, Drawer, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { RxCross2 } from 'react-icons/rx';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import PasswordInput from 'components/Common/PasswordInput';
import Radio from 'components/Common/Radio';
import TelInput from 'components/Common/TelInput';
import { useCreateTelephonyEndpoint } from '@/apis/business/setting/telephonyEndpoints/hook';
import SipTrunkingConfig from '@/pages/Business/Agent/Configure/DeployPhone/byoc/components/SipTrunkingConfig';
import { InboundSipTrunkResponse } from 'apis/business/agent/deployPhone/inboundSipTrunk/types';
import { OutboundSipTrunkResponse } from 'apis/business/agent/deployPhone/outBoundSipTrunk/types';
import { countryCodeFromPhoneNumber } from '@/utils/phoneNumberBilling';

type Props = {
  open: boolean;
  onClose: () => void;
};

type SipTab = 'registration' | 'trunking';

type SipFormValues = {
  number_type: string;
  phone_number: string;
  extension: string;
  main_line_number: string;
  friendly_name: string;
  username: string;
  password: string;
  authentication_username: string;
  proxy: string;
  outbound_proxy: string;
  realm: string;
  from_domain: string;
};

type TrunkingToggleValues = {
  inbound_configured: boolean;
  outbound_configured: boolean;
};

const DEFAULT_VALUES: SipFormValues = {
  number_type: 'phone',
  phone_number: '',
  extension: '',
  main_line_number: '',
  friendly_name: '',
  username: '',
  password: '',
  authentication_username: '',
  proxy: '',
  outbound_proxy: '',
  realm: '',
  from_domain: ''
};

const helperSx = { fontSize: 12, color: '#6B7280', mt: 0.75, mb: 2.25, lineHeight: 1.45 };
const shrinkLabel = { shrink: true };

function TabButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1,
        textAlign: 'center',
        py: 1.25,
        px: 2,
        cursor: 'pointer',
        bgcolor: active ? '#E8F1FF' : '#fff',
        color: active ? 'primary.main' : '#111827',
        fontWeight: 600,
        fontSize: 14,
        borderRight: '1px solid #E5E7EB',
        '&:last-of-type': { borderRight: 'none' },
        transition: 'all 0.15s ease'
      }}
    >
      {label}
    </Box>
  );
}

function firstPhoneNumber(numbers?: unknown): string {
  if (!Array.isArray(numbers) || !numbers.length) return '';
  const first = numbers[0];
  if (typeof first === 'string') return first.trim();
  if (first && typeof first === 'object' && 'value' in first) {
    return String((first as { value?: string }).value || '').trim();
  }
  return '';
}

export default function ConnectViaSipDrawer({ open, onClose }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState<SipTab>('registration');
  const { control, reset, handleSubmit, watch } = useForm<SipFormValues>({ defaultValues: DEFAULT_VALUES });
  const {
    control: trunkingControl,
    reset: resetTrunking,
    watch: watchTrunking
  } = useForm<TrunkingToggleValues>({
    defaultValues: { inbound_configured: true, outbound_configured: true }
  });
  const { mutateAsync: createEndpoint, isPending } = useCreateTelephonyEndpoint();
  const [inboundDraft, setInboundDraft] = useState<InboundSipTrunkResponse | null>(null);
  const [outboundDraft, setOutboundDraft] = useState<OutboundSipTrunkResponse | null>(null);
  const numberType = watch('number_type');
  const isExtension = numberType === 'extension';
  const inboundConfigured = watchTrunking('inbound_configured');
  const outboundConfigured = watchTrunking('outbound_configured');
  const canConnectTrunk =
    (inboundConfigured && Boolean(inboundDraft)) || (outboundConfigured && Boolean(outboundDraft));

  useEffect(() => {
    if (open) {
      reset(DEFAULT_VALUES);
      resetTrunking({ inbound_configured: true, outbound_configured: true });
      setInboundDraft(null);
      setOutboundDraft(null);
      setTab('registration');
    }
  }, [open, reset, resetTrunking]);

  useEffect(() => {
    if (!inboundConfigured) setInboundDraft(null);
  }, [inboundConfigured]);

  useEffect(() => {
    if (!outboundConfigured) setOutboundDraft(null);
  }, [outboundConfigured]);

  const persistInbound = async (payload: InboundSipTrunkResponse) => {
    const phoneNumber = firstPhoneNumber(payload.additional_config?.numbers);
    if (!phoneNumber) throw new Error('Phone number is required');
    setInboundDraft(payload);
  };

  const persistOutbound = async (payload: OutboundSipTrunkResponse) => {
    const phoneNumber = firstPhoneNumber(payload.additional_config?.numbers);
    if (!phoneNumber) throw new Error('Phone number is required');
    setOutboundDraft(payload);
  };

  const onConnectTrunkNumber = async () => {
    if (!canConnectTrunk) return;

    const inbound = inboundConfigured ? inboundDraft : null;
    const outbound = outboundConfigured ? outboundDraft : null;
    const phoneNumber =
      firstPhoneNumber(inbound?.additional_config?.numbers) || firstPhoneNumber(outbound?.additional_config?.numbers);
    if (!phoneNumber) return;

    const friendlyName = inbound?.name?.trim() || outbound?.name?.trim() || undefined;
    // TelInput defaultCountry is US; incomplete +1 numbers must still send country_code.
    const countryCode = countryCodeFromPhoneNumber(phoneNumber, 'US');

    await createEndpoint({
      telephony_endpoint: {
        kind: 'sip_trunk',
        phone_number: phoneNumber,
        provider: 'sip_connectivity',
        ...(countryCode ? { country_code: countryCode } : {}),
        friendly_name: friendlyName,
        connection_config: {
          type: 'sip_trunking',
          ...(inbound
            ? {
                inbound: {
                  name: inbound.name,
                  ...(inbound.additional_config || {})
                }
              }
            : {}),
          ...(outbound
            ? {
                outbound: {
                  name: outbound.name,
                  ...(outbound.additional_config || {})
                }
              }
            : {})
        }
      }
    });
    onClose();
  };

  const onSubmitRegistration = handleSubmit(async (values) => {
    const kind = values.number_type === 'extension' ? 'extension' : 'sip_registration';
    const phoneNumber = values.number_type === 'extension' ? values.extension.trim() : values.phone_number.trim();
    const countrySource =
      values.number_type === 'extension' ? values.main_line_number.trim() : values.phone_number.trim();
    const countryCode = countryCodeFromPhoneNumber(countrySource, 'US');

    // Register Line: submit SIP creds to Fusion, persist gateway + registered/failed status.
    // LiveKit / agent destinations are created later when an agent is assigned.
    await createEndpoint({
      telephony_endpoint: {
        kind,
        phone_number: phoneNumber,
        provider: 'sip_connectivity',
        ...(countryCode ? { country_code: countryCode } : {}),
        friendly_name: values.friendly_name.trim() || undefined,
        connection_config: {
          type: 'sip_registration',
          number_type: values.number_type,
          ...(values.number_type === 'extension' ? { main_line_number: values.main_line_number.trim() } : {}),
          username: values.username,
          password: values.password,
          auth_username: values.username,
          auth_password: values.password,
          authentication_username: values.authentication_username,
          proxy: values.proxy,
          outbound_proxy: values.outbound_proxy || values.proxy,
          realm: values.realm,
          from_domain: values.from_domain
        }
      }
    });
    onClose();
  });

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 720,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2.5}
        borderBottom='1px solid #E5E7EB'
      >
        <Typography fontSize={18} fontWeight={500} color='text.primary'>
          Connect via SIP
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close' sx={{ color: 'text.primary' }}>
          <RxCross2 size={20} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={3} py={2.5}>
        <Box display='flex' mb={3} border='1px solid #E5E7EB' borderRadius='12px' overflow='hidden'>
          <TabButton active={tab === 'registration'} label='SIP Registration' onClick={() => setTab('registration')} />
          <TabButton active={tab === 'trunking'} label='SIP Trunking' onClick={() => setTab('trunking')} />
        </Box>

        {tab === 'registration' ? (
          <Box>
            <Typography fontWeight={700} fontSize={15} color='#111827' mb={1.5}>
              SIP Registration
            </Typography>

            <Radio
              name='number_type'
              control={control}
              label=' '
              options={[
                { label: 'Phone', value: 'phone' },
                { label: 'Extension', value: 'extension' }
              ]}
            />

            {isExtension ? (
              <>
                <Input
                  name='extension'
                  control={control}
                  label='Extension'
                  placeholder='e.g. 121'
                  fullWidth
                  required
                  InputLabelProps={shrinkLabel}
                />
                <Typography sx={helperSx}>The extension this SIP account belongs to.</Typography>

                <TelInput
                  name='main_line_number'
                  control={control}
                  label='Main Line Number'
                  fullWidth
                  required
                  defaultCountry='US'
                  preferredCountries={['US', 'CA']}
                  InputLabelProps={shrinkLabel}
                />
                <Typography sx={helperSx}>Enter the mainline number corresponding to the above extension.</Typography>
              </>
            ) : (
              <>
                <TelInput
                  name='phone_number'
                  control={control}
                  label='Phone Number'
                  fullWidth
                  required
                  defaultCountry='CA'
                  preferredCountries={['CA', 'US']}
                  InputLabelProps={shrinkLabel}
                />
                <Typography sx={helperSx}>
                  The number this SIP account belongs to. Used as your caller ID when making calls.
                </Typography>
              </>
            )}

            <Input
              name='friendly_name'
              control={control}
              label='Friendly Name'
              fullWidth
              InputLabelProps={shrinkLabel}
            />
            <Typography sx={helperSx}>Shown instead of the number in your lists. You can change it anytime.</Typography>

            <Input name='username' control={control} label='Username' fullWidth InputLabelProps={shrinkLabel} />
            <Typography sx={helperSx}>
              Your SIP account login — this is what identifies your device to the server.
            </Typography>

            <PasswordInput name='password' control={control} label='Password' fullWidth InputLabelProps={shrinkLabel} />
            <Typography sx={helperSx}>The password for your SIP account.</Typography>

            <Input
              name='authentication_username'
              control={control}
              label='Authentication Username'
              fullWidth
              InputLabelProps={shrinkLabel}
            />
            <Typography sx={helperSx}>
              Only needed if your provider gives you a separate ID for authentication, different from your regular
              username.
            </Typography>

            <Input name='proxy' control={control} label='Proxy' fullWidth InputLabelProps={shrinkLabel} />
            <Typography sx={helperSx}>
              The main SIP server your app connects and registers to. This is the only field required to get connected.
            </Typography>

            <Input
              name='outbound_proxy'
              control={control}
              label='Outbound Proxy'
              fullWidth
              InputLabelProps={shrinkLabel}
            />
            <Typography sx={helperSx}>
              Forces all outgoing calls through this server instead of the default route. Leave blank unless your
              provider specifically requires it.
            </Typography>

            <Input name='realm' control={control} label='Realm' fullWidth InputLabelProps={shrinkLabel} />
            <Typography sx={helperSx}>
              Used only during login/authentication to verify your credentials. Usually same as your Proxy address —
              leave blank to auto-detect.
            </Typography>

            <Input name='from_domain' control={control} label='From Domain' fullWidth InputLabelProps={shrinkLabel} />
            <Typography sx={{ ...helperSx, mb: 0 }}>
              The domain shown to the other party when you make a call. Usually same as your Proxy address — leave blank
              to auto-detect.
            </Typography>
          </Box>
        ) : (
          <>
            <SipTrunkingConfig
              control={trunkingControl}
              inboundConfigured={inboundConfigured}
              outboundConfigured={outboundConfigured}
              inboundFormData={undefined}
              outboundFormData={undefined}
              selectedProvider='sip_connectivity'
              deleteInboundFormData={() => setInboundDraft(null)}
              deleteOutboundFormData={() => setOutboundDraft(null)}
              persistInbound={persistInbound}
              persistOutbound={persistOutbound}
            />
            <Box mt={1} mb={1} px={2} py={1.5} borderRadius='8px' bgcolor='#F8FAFC' border='1px solid #E5E7EB'>
              <Typography fontSize={13} color='#6B7280' lineHeight={1.5}>
                After connecting, verify the route with your provider before assigning this number to an agent.
              </Typography>
            </Box>
            {(inboundDraft || outboundDraft) && (
              <Typography fontSize={12} color='#059669' mt={0.5}>
                {[
                  inboundDraft ? 'Inbound configuration saved' : null,
                  outboundDraft ? 'Outbound configuration saved' : null
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            )}
          </>
        )}
      </Box>

      <Box
        display='flex'
        justifyContent='flex-end'
        gap={1.5}
        px={3}
        py={2.5}
        borderTop='1px solid #E5E7EB'
        bgcolor='#fff'
      >
        <Button
          variant='outlined'
          onClick={onClose}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: '#E5E7EB',
            color: '#111827',
            fontWeight: 600,
            px: 2.5,
            bgcolor: '#fff',
            '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' }
          }}
        >
          Cancel
        </Button>
        {tab === 'registration' ? (
          <Button
            variant='contained'
            color='primary'
            onClick={onSubmitRegistration}
            isPending={isPending}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, boxShadow: 'none', px: 2.5 }}
          >
            Register Line
          </Button>
        ) : (
          <Button
            variant='contained'
            color='primary'
            onClick={onConnectTrunkNumber}
            isPending={isPending}
            disabled={!canConnectTrunk}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, boxShadow: 'none', px: 2.5 }}
          >
            Connect Number
          </Button>
        )}
      </Box>
    </Drawer>
  );
}
